import React from 'react'

import { useNavigate } from '@lib/react-router-dom'

import { NotificationPreview } from '@pages/Profile/Notifications/NotificationPreview'

import { Carousel } from '@ui/Carousel/Carousel'
import { CarouselChild } from '@ui/Carousel/CarouselItem'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { useWindowResize } from '@utils/domUtils'
import { useNotificationUnread } from '@utils/notifications'
import { isOne, isZero } from '@utils/validations'

import { Notification } from '@model/Notification'

import styles from './NotificationSlider.module.scss'

interface NotificationSliderProps {
  notifications: Notification[]
  hideNotifications: () => void
}

export const NotificationSlider: React.FC<NotificationSliderProps> = ({
  notifications,
  hideNotifications,
}) => {
  const { refreshAllNotifications } = useNotificationUnread()
  const isMobile = useWindowResize()
  const apiClient = useApiClient(ClientApiClient)
  const navigate = useNavigate()

  const readNotification = async (notification: Notification) => {
    if (!notification.isRead) {
      await apiClient.readNotification(notification.id, notification.clientNotificationId)
      refreshAllNotifications()
    }
  }

  const openNotification = ({ id, clientNotificationId }: Notification) => {
    const notificationPath = `/profile/notifications/${id}${
      clientNotificationId ? `/${clientNotificationId}` : ''
    }`
    return navigate(notificationPath)
  }

  if (isZero(notifications.length)) {
    return null
  }
  if (isOne(notifications.length)) {
    const notification = notifications[0]
    return (
      <NotificationPreview
        read={() => readNotification(notification)}
        open={() => openNotification(notification)}
        hide={hideNotifications}
        notification={notification}
        externalMode
      />
    )
  }

  if (isMobile) {
    return (
      <div className={styles.mobileCarousel}>
        {notifications.map((notification) => (
          <NotificationPreview
            key={notification.id}
            hide={hideNotifications}
            read={() => readNotification(notification)}
            open={() => openNotification(notification)}
            externalMode
            notification={notification}
          />
        ))}
      </div>
    )
  }

  return (
    <Carousel loop showArrows className={styles.carousel} interval={5000}>
      {notifications.map((notification) => (
        <CarouselChild key={notification.id} className={styles.carouselChild}>
          <NotificationPreview
            hide={hideNotifications}
            read={() => readNotification(notification)}
            open={() => openNotification(notification)}
            externalMode
            notification={notification}
          />
        </CarouselChild>
      ))}
    </Carousel>
  )
}
